// views/Dashboard/PainelExecutivo/sections/ceo/EnergiaDasUsinas.tsx
//
// "Energia injetada por cada usina e porcentagem utilizada" — dita do jeito que
// o Igor pergunta: de cada usina, quanto PRODUZIU (contra a meta) e, do que
// produziu, quanto a PRÓPRIA usina consumiu, quanto foi PARA OS OUTROS (clientes
// do rateio) e quanto está SOBRANDO (virou saldo de crédito, energia parada).
//
// As três partes somam 100% do que foi produzido. A conta é `fluxoEnergia`.
import React, { useMemo } from 'react';
import { faixaDoAtingimento, financeiroPorUsina, fluxoEnergia, geracaoPorUsina, isoDoMes, mesDoOrdinal, ordinalDoMes } from '../../../../../src/lib/dashboard/painelCeo';
import type { LinhaVsMeta, UsinaCeo } from '../../../../../src/lib/dashboard/painelCeo';
import { parteVigorDaFatura } from '../../../../../src/lib/financeiro/parteVigor';
import { useFinanceiroDashboardData } from '../financeiro/useFinanceiroDashboardData';
import { Bloco, COR_FAIXA, ErroBloco, LinhasCarregando, PassoMes, kwhCurto, pct, useMontado, CURVA_SAIDA } from './ui';

const COR = {
  proprio: '#9CA3AF',
  outros: '#55702A',
  sobrando: 'repeating-linear-gradient(-45deg, rgba(242,153,74,0.85) 0 3px, rgba(242,153,74,0.35) 3px 7px)',
};

interface Props {
  usinas: UsinaCeo[];
  fator: number;
  linhas: LinhaVsMeta[];
  mes: string;
  onMes: (mes: string) => void;
  /** Mais antigo que dá para escolher (a história da telemetria). */
  ordMinimo: number;
  ordAtual: number;
  carregando: boolean;
}

const GRADE = 'md:grid md:grid-cols-[minmax(150px,1fr)_104px_repeat(3,84px)_minmax(160px,1.6fr)] md:items-center md:gap-4';

const EnergiaDasUsinas: React.FC<Props> = ({ usinas, fator, linhas, mes, onMes, ordMinimo, ordAtual, carregando }) => {
  const { dados, percentualVigorPorParceira, loading, erro } = useFinanceiroDashboardData();
  const ord = ordinalDoMes(mes) ?? ordAtual - 1;
  const mesIso = isoDoMes(mes) ?? '';

  const fluxo = useMemo(() => {
    const fin = financeiroPorUsina(dados, usinas, mes, (f) => parteVigorDaFatura(f, percentualVigorPorParceira));
    const doMes = linhas.filter((l) => String(l.mes_ref ?? '').slice(0, 10) === mesIso);
    return fluxoEnergia(usinas, fin, geracaoPorUsina(usinas, doMes, fator));
  }, [dados, usinas, mes, mesIso, linhas, fator, percentualVigorPorParceira]);

  const tot = fluxo.reduce(
    (t, f) => ({
      produzido: t.produzido + (f.produzidoKwh ?? 0),
      proprio: t.proprio + f.consumoProprioKwh,
      outros: t.outros + f.usadoClientesKwh,
      sobrando: t.sobrando + f.sobrandoKwh,
    }),
    { produzido: 0, proprio: 0, outros: 0, sobrando: 0 },
  );
  const parte = (v: number) => (tot.produzido > 0 ? v / tot.produzido : null);

  const espera = (loading && dados.length === 0) || carregando;

  return (
    <Bloco
      titulo={espera || erro || tot.produzido === 0
        ? 'Para onde vai a energia de cada usina'
        : `Em ${mes}, ${pct(parte(tot.outros))} da energia produzida foi para os clientes`}
      subtitulo={espera || tot.produzido === 0 ? 'Produzido, consumo próprio, mandado para os outros e o que sobra.'
        : `${pct(parte(tot.sobrando))} sobrou como saldo de crédito e ${pct(parte(tot.proprio))} ficou com as próprias usinas · ${kwhCurto(tot.produzido)} produzidos`}
      acoes={
        <PassoMes
          mes={mes}
          podeVoltar={ord > ordMinimo}
          podeAvancar={ord < ordAtual}
          onVoltar={() => onMes(mesDoOrdinal(ord - 1))}
          onAvancar={() => onMes(mesDoOrdinal(ord + 1))}
        />
      }
    >
      {erro ? (
        <ErroBloco mensagem={erro} />
      ) : espera ? (
        <LinhasCarregando linhas={6} rotulo="Cruzando a telemetria com as contas de luz…" />
      ) : (
        <>
          <div className={`hidden border-y border-gray-100 bg-gray-50/70 px-5 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-400 ${GRADE}`}>
            <span>Usina</span>
            <span className="text-right">Produziu</span>
            <span className="text-right">Própria</span>
            <span className="text-right">Pros outros</span>
            <span className="text-right">Sobrando</span>
            <span>Do que produziu</span>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700/70">
            {usinas.map((u, i) => <Linha key={u.id} nome={u.nome} f={fluxo[i]} ordem={i} />)}
          </ul>
          <footer className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-t border-gray-100 px-5 py-3 text-[11px] leading-relaxed text-gray-500 dark:border-gray-700 dark:text-gray-400">
            <span className="flex flex-wrap gap-x-4 gap-y-1">
              <Amostra estilo={{ background: COR.proprio }} rotulo="Consumo da própria usina" />
              <Amostra estilo={{ background: COR.outros }} rotulo="Usado pelos clientes do rateio" />
              <Amostra estilo={{ backgroundImage: COR.sobrando }} rotulo="Sobrando (virou saldo)" />
            </span>
            <span className="max-w-xl">
              Aproximado: a produção é do mês do calendário e as contas, do ciclo de leitura de cada cliente. O consumo próprio vem
              da conta de luz da usina; sem ela no sistema (*), é o produzido menos o injetado.
            </span>
          </footer>
        </>
      )}
    </Bloco>
  );
};

const Linha: React.FC<{ nome: string; f: ReturnType<typeof fluxoEnergia>[number]; ordem: number }> = ({ nome, f, ordem }) => {
  const montado = useMontado();
  const base = f.produzidoKwh ?? 0;
  const p = (v: number) => (base > 0 ? v / base : null);
  const faixa = faixaDoAtingimento(f.atingimento);
  const segs = [
    { v: f.consumoProprioKwh, estilo: { background: COR.proprio } },
    { v: f.usadoClientesKwh, estilo: { background: COR.outros } },
    { v: f.sobrandoKwh, estilo: { backgroundImage: COR.sobrando } },
  ];

  return (
    <li className={`px-5 py-3 transition-colors duration-150 hover:bg-gray-50/80 dark:hover:bg-gray-900/30 ${GRADE}`}>
      <div className="flex items-baseline justify-between gap-3 md:block">
        <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">{nome}</p>
        <p className="text-[11px] tabular-nums text-gray-400">{base > 0 ? kwhCurto(base) : 'sem produção medida'}</p>
      </div>
      <p className="mt-1 text-right text-sm tabular-nums md:mt-0">
        <span className="text-[11px] text-gray-400 md:hidden">Produziu </span>
        <strong className={`font-bold ${COR_FAIXA[faixa].texto}`}>{pct(f.atingimento)}</strong>
        <span className="block text-[10px] text-gray-400">da meta</span>
      </p>
      <Percentual rotulo="Própria usina" valor={p(f.consumoProprioKwh)} marca={!f.consumoDaConta} />
      <Percentual rotulo="Pros outros" valor={p(f.usadoClientesKwh)} forte />
      <Percentual rotulo="Sobrando" valor={p(f.sobrandoKwh)} alerta={(p(f.sobrandoKwh) ?? 0) >= 0.5} />
      <div className="mt-2 md:mt-0">
        <div
          className="flex h-3 gap-[2px] overflow-hidden rounded-full bg-gray-100 motion-reduce:!transition-none dark:bg-gray-700/60"
          style={{ clipPath: `inset(0 ${montado ? 0 : 100}% 0 0 round 9999px)`, transition: `clip-path 700ms ${CURVA_SAIDA} ${Math.min(ordem, 12) * 35}ms` }}
          role="img"
          aria-label={`${nome}: própria ${pct(p(f.consumoProprioKwh))}, outros ${pct(p(f.usadoClientesKwh))}, sobrando ${pct(p(f.sobrandoKwh))}`}
        >
          {base > 0 && segs.filter((s) => s.v > 0).map((s, k) => (
            <span key={k} className="h-full" style={{ width: `${(s.v / base) * 100}%`, ...s.estilo }} />
          ))}
        </div>
        {f.tiradoDoSaldoKwh > 0 && (
          <p className="mt-1 text-[10px] text-gray-500 dark:text-gray-400">
            Os clientes usaram mais {kwhCurto(f.tiradoDoSaldoKwh)} do saldo de meses anteriores.
          </p>
        )}
      </div>
    </li>
  );
};

const Percentual: React.FC<{ rotulo: string; valor: number | null; forte?: boolean; alerta?: boolean; marca?: boolean }> = ({ rotulo, valor, forte, alerta, marca }) => (
  <p className="mt-1 flex items-baseline justify-between text-sm tabular-nums md:mt-0 md:block md:text-right">
    <span className="text-[11px] text-gray-400 md:hidden">{rotulo}</span>
    <span className={`${forte ? 'font-bold text-gray-900 dark:text-white' : alerta ? 'font-bold text-orange-600 dark:text-orange-400' : 'font-medium text-gray-600 dark:text-gray-300'}`}>
      {pct(valor)}{marca && <span className="text-gray-400" title="Sem a conta de luz da usina no sistema: produzido menos injetado"> *</span>}
    </span>
  </p>
);

const Amostra: React.FC<{ estilo: React.CSSProperties; rotulo: string }> = ({ estilo, rotulo }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="h-2.5 w-2.5 rounded-[3px]" style={estilo} aria-hidden /> {rotulo}
  </span>
);

export default EnergiaDasUsinas;
